/* Mocked in-memory implementation of Tasks Clients for local development */

import {
  IOperationalDaysClient,
  IOperationalOverviewClient,
  ITasksClient,
  ITaskTemplatesClient,
  ITaskCategoriesClient,
  ITaskStatusesClient,
  ITaskPrioritiesClient,
  ITaskSchedulesClient,
  ITaskReportingDashboardClient,
} from './ITasksClient';
import {
  OperationalDayTemplateDto,
  CreateUpdateOperationalDayTemplateRequest,
  TaskTemplateDto,
  TaskTemplateVersionDto,
  TaskCategoryDto,
  TaskPriorityDto,
  TaskStatusDto,
  TaskScheduleDto,
  TaskDashboardResponse,
  TaskStatusMetric,
  TaskPriorityMetric,
  TaskOverdueMetric,
  TaskCompletionTrendPoint,
  OperationalOverviewResponse,
  OperationalTimelineItem,
  TaskSummaryDto,
  TaskDetailDto,
  TaskEvidenceDto,
  TaskEventDto,
  SuccessResponse,
  CreateTaskRequest,
  ChangePriorityRequest,
  BulkChangePriorityRequest,
} from './tasksTypes';
import { tasksMockStore } from './tasksMockData';

/* ---- Helper mappers ---- */
function getActiveTemplateVersion(templateId: number): TaskTemplateVersionDto | undefined {
  const template = tasksMockStore.taskTemplates.find(t => t.id === templateId);
  return template?.versions.find(v => v.isActive) ?? template?.versions[0];
}

function toSummary(t: TaskDetailDto): TaskSummaryDto {
  const dueDate = t.dueDateUtc ? new Date(t.dueDateUtc) : null;
  const isOverdue = !!dueDate && dueDate.getTime() < Date.now() && t.statusId !== 3;
  const templateVersion = t.templateName 
    ? getActiveTemplateVersion(tasksMockStore.taskTemplates.find(tpl => tpl.name === t.templateName)?.id ?? 0)
    : undefined;
  const category = t.categoryId ? tasksMockStore.taskCategories.find(c => c.id === t.categoryId) : null;

  return {
    id: t.id,
    title: t.title,
    storeNumber: t.storeNumber,
    storeName: t.storeName,
    targetType: 'Location',
    categoryName: category?.name ?? 'General',
    personaName: t.targetRoleName ?? 'Store Manager',
    assignedUserId: t.targetUserId ?? null,
    assignedUserName: t.targetUserName ?? 'Alex Smith',
    taskSourceType: t.taskSourceType ?? 'Scheduled',
    statusId: t.statusId,
    defaultPriorityId: templateVersion?.defaultTaskPriorityId ?? null,
    priorityId: t.priorityId,
    startDateUtc: t.startDateUtc,
    dueDateUtc: t.dueDateUtc,
    completedDateUtc: t.completedDateUtc,
    isOverdue,
    percentageComplete: t.percentageComplete,
  };
}

/* ---- Mocked Clients ---- */

export class OperationalDaysClientMocked implements IOperationalDaysClient {
  getOperationalDays(isActive?: boolean): Promise<SuccessResponse<OperationalDayTemplateDto[]>> {
    let result = [...tasksMockStore.operationalDays];
    if (typeof isActive === 'boolean') {
      result = result.filter(o => o.isActive === isActive);
    }
    return Promise.resolve({ data: result });
  }

  getOperationalDayById(id: number): Promise<SuccessResponse<OperationalDayTemplateDto>> {
    const found = tasksMockStore.operationalDays.find(o => o.id === id);
    if (!found) return Promise.reject(new Error('Not found'));
    return Promise.resolve({ data: found });
  }

  createOperationalDay(req: CreateUpdateOperationalDayTemplateRequest): Promise<SuccessResponse<OperationalDayTemplateDto>> {
    const created: OperationalDayTemplateDto = { id: tasksMockStore.nextId(), ...req };
    tasksMockStore.operationalDays.push(created);
    return Promise.resolve({ data: created });
  }

  updateOperationalDay(id: number, req: CreateUpdateOperationalDayTemplateRequest): Promise<void> {
    tasksMockStore.operationalDays = tasksMockStore.operationalDays.map(o => (o.id === id ? { ...o, ...req, id } : o));
    return Promise.resolve();
  }

  deleteOperationalDay(id: number): Promise<void> {
    tasksMockStore.operationalDays = tasksMockStore.operationalDays.filter(o => o.id !== id);
    return Promise.resolve();
  }
}

export class OperationalOverviewClientMocked implements IOperationalOverviewClient {
  getOverview(storeNumber: string): Promise<SuccessResponse<OperationalOverviewResponse>> {
    const tasksForStore = tasksMockStore.tasks.filter(t => t.storeNumber === storeNumber);
    const items: OperationalTimelineItem[] = tasksForStore.map(t => {
      const category = t.categoryId ? tasksMockStore.taskCategories.find(c => c.id === t.categoryId) : null;
      return {
        taskId: t.id,
        title: t.title,
        taskTemplateId: t.templateId ?? null,
        categoryName: category?.name ?? 'General',
        priorityName: tasksMockStore.taskPriorities.find(p => p.id === t.priorityId)?.name ?? 'Medium',
        taskPriorityId: t.priorityId,
        taskStatusId: t.statusId,
        statusName: tasksMockStore.taskStatuses.find(s => s.id === t.statusId)?.name ?? 'Open',
        dueDateTimeUtc: t.dueDateUtc,
        startDateTimeUtc: t.startDateUtc,
        completedDateTimeUtc: t.completedDateUtc,
        percentageComplete: t.percentageComplete,
        isOverdue: toSummary(t).isOverdue,
        assignedUserId: t.targetUserId ?? null,
      };
    });

    const response: OperationalOverviewResponse = {
      storeNumber,
      day: new Date().toISOString().substring(0, 10),
      lanes: [{ laneType: 'Scheduled', items }],
      totalTasks: items.length,
      completedTasks: items.filter(i => i.statusName === 'Completed').length,
      overdueTasks: items.filter(i => i.isOverdue).length,
    };

    return Promise.resolve({ data: response });
  }

  bulkReassign(): Promise<void> { return Promise.resolve(); }
  bulkPriority(): Promise<void> { return Promise.resolve(); }
}

export class TasksClientMocked implements ITasksClient {
  getStoreTasks(storeNumber: string, franchiseName: string, fromDueUtc?: string | Date, toDueUtc?: string | Date): Promise<TaskSummaryDto[]> {
    console.log('[TasksClientMocked] getStoreTasks called with:', { storeNumber, franchiseName, fromDueUtc, toDueUtc });
    console.log('[TasksClientMocked] Total tasks in mockStore:', tasksMockStore.tasks.length);
    console.log('[TasksClientMocked] Sample task store numbers:', tasksMockStore.tasks.map(t => ({ id: t.id, storeNumber: t.storeNumber, dueDate: t.dueDateUtc })));
    
    let result = tasksMockStore.tasks.filter(t => t.storeNumber === storeNumber);
    console.log('[TasksClientMocked] After store filter (storeNumber=' + storeNumber + '):', result.length, result.map(t => t.id));
    
    // Filter by date range if provided
    if (fromDueUtc) {
      const fromDate = typeof fromDueUtc === 'string' ? new Date(fromDueUtc) : fromDueUtc;
      console.log('[TasksClientMocked] Filtering fromDueUtc:', fromDate.toISOString());
      const beforeFilter = result.length;
      result = result.filter(t => {
        if (!t.dueDateUtc) return true;
        const taskDue = new Date(t.dueDateUtc);
        const passes = taskDue >= fromDate;
        if (!passes) console.log('[TasksClientMocked]   Task', t.id, 'filtered out:', taskDue.toISOString(), '< ', fromDate.toISOString());
        return passes;
      });
      console.log('[TasksClientMocked] After fromDueUtc filter:', beforeFilter, '->', result.length);
    }
    if (toDueUtc) {
      const toDate = typeof toDueUtc === 'string' ? new Date(toDueUtc) : toDueUtc;
      console.log('[TasksClientMocked] Filtering toDueUtc:', toDate.toISOString());
      const beforeFilter = result.length;
      result = result.filter(t => {
        if (!t.dueDateUtc) return true;
        const taskDue = new Date(t.dueDateUtc);
        const passes = taskDue <= toDate;
        if (!passes) console.log('[TasksClientMocked]   Task', t.id, 'filtered out:', taskDue.toISOString(), '>', toDate.toISOString());
        return passes;
      });
      console.log('[TasksClientMocked] After toDueUtc filter:', beforeFilter, '->', result.length);
    }
    
    const summaries = result.map(toSummary);
    console.log('[TasksClientMocked] Returning', summaries.length, 'task summaries');
    console.log('[TasksClientMocked] Summary details:', summaries.map(s => ({ id: s.id, title: s.title, sourceType: s.taskSourceType, dueDate: s.dueDateUtc })));
    return Promise.resolve(summaries);
  }

  getTask(taskId: number): Promise<TaskDetailDto> {
    const found = tasksMockStore.tasks.find(t => t.id === taskId);
    if (!found) return Promise.reject(new Error('Not found'));
    return Promise.resolve(found);
  }

  getEvidence(taskId: number): Promise<TaskEvidenceDto[]> {
    const found = tasksMockStore.tasks.find(t => t.id === taskId);
    return Promise.resolve(found?.evidence ?? []);
  }

  getAudit(taskId: number): Promise<TaskEventDto[]> {
    const found = tasksMockStore.tasks.find(t => t.id === taskId);
    return Promise.resolve(found?.history ?? []);
  }

  createTask(req: CreateTaskRequest): Promise<number> {
    const id = tasksMockStore.nextId();
    const now = new Date().toISOString();
    const version = req.taskTemplateId ? getActiveTemplateVersion(req.taskTemplateId) : undefined;
    const defaultPriority = version?.defaultTaskPriorityId ?? req.taskPriorityId;
    
    const detail: TaskDetailDto = {
      id,
      templateId: req.taskTemplateId ?? null,
      title: req.title,
      description: req.description ?? '',
      storeNumber: req.storeNumber,
      storeName: req.storeName ?? req.storeNumber,
      statusId: 1,
      priorityId: req.taskPriorityId,
      defaultPriorityId: defaultPriority,
      percentageComplete: 0,
      dueDateUtc: req.dueDateTimeUtc ?? null,
      startDateUtc: null,
      completedDateUtc: null,
      templateName: tasksMockStore.taskTemplates.find(t => t.id === req.taskTemplateId)?.name ?? null,
      targetUserId: req.targetUserId ?? null,
      targetUserName: req.targetUserName ?? null,
      targetRoleId: req.targetRoleId ?? null,
      targetRoleName: req.targetRoleName ?? null,
      steps: version?.steps ?? [],
      evidence: [],
      history: [
        {
          id: tasksMockStore.nextId(),
          eventType: 'Created',
          oldValue: null,
          newValue: JSON.stringify({ ...req, taskTemplateVersionId: version?.id }),
          comment: null,
          createdAtUtc: now,
          createdByUserId: null,
        },
      ],
      requiresApproval: version?.requiresApproval ?? false,
      approverUserId: version?.defaultApproverUserId ?? null,
      approverPersonaId: version?.defaultApproverPersonaId ?? null,
    };
    tasksMockStore.tasks.push(detail);
    return Promise.resolve(id);
  }

  createTasksBulk(reqs: CreateTaskRequest[]): Promise<number[]> {
    const ids: number[] = [];
    reqs.forEach(r => {
      const id = tasksMockStore.nextId();
      const version = r.taskTemplateId ? getActiveTemplateVersion(r.taskTemplateId) : undefined;
      const defaultPriority = version?.defaultTaskPriorityId ?? r.taskPriorityId;
      
      const detail: TaskDetailDto = {
        id,
        templateId: r.taskTemplateId ?? null,
        title: r.title,
        description: r.description ?? '',
        storeNumber: r.storeNumber,
        storeName: r.storeName ?? r.storeNumber,
        statusId: 1,
        priorityId: r.taskPriorityId,
        defaultPriorityId: defaultPriority,
        percentageComplete: 0,
        dueDateUtc: r.dueDateTimeUtc ?? null,
        startDateUtc: null,
        completedDateUtc: null,
        templateName: tasksMockStore.taskTemplates.find(t => t.id === r.taskTemplateId)?.name ?? null,
        targetUserId: r.targetUserId ?? null,
        targetUserName: r.targetUserName ?? null,
        targetRoleId: r.targetRoleId ?? null,
        targetRoleName: r.targetRoleName ?? null,
        steps: version?.steps ?? [],
        evidence: [],
        history: [
          {
            id: tasksMockStore.nextId(),
            eventType: 'Created',
            oldValue: null,
            newValue: JSON.stringify({ ...r, taskTemplateVersionId: version?.id }),
            comment: null,
            createdAtUtc: new Date().toISOString(),
            createdByUserId: null,
          },
        ],
        requiresApproval: version?.requiresApproval ?? false,
        approverPersonaId: version?.defaultApproverPersonaId ?? null,
        approverUserId: version?.defaultApproverUserId ?? null,
      };
      tasksMockStore.tasks.push(detail);
      ids.push(id);
    });
    return Promise.resolve(ids);
  }

  reassignTask(): Promise<void> { return Promise.resolve(); }
  bulkReassign(): Promise<void> { return Promise.resolve(); }

  changePriority(taskId: number, req: ChangePriorityRequest): Promise<void> {
    const priority = tasksMockStore.taskPriorities.find(p => p.id === req.newPriorityId);
    if (!priority) return Promise.resolve();
    tasksMockStore.tasks = tasksMockStore.tasks.map(t => (t.id === taskId ? { ...t, priorityId: req.newPriorityId } : t));
    return Promise.resolve();
  }

  bulkChangePriority(req: BulkChangePriorityRequest): Promise<void> {
    const priority = tasksMockStore.taskPriorities.find(p => p.id === req.newPriorityId);
    if (!priority) return Promise.resolve();
    tasksMockStore.tasks = tasksMockStore.tasks.map(t => (req.taskIds.includes(t.id) ? { ...t, priorityId: req.newPriorityId } : t));
    return Promise.resolve();
  }

  deleteTask(taskId: number): Promise<void> {
    tasksMockStore.tasks = tasksMockStore.tasks.filter(t => t.id !== taskId);
    return Promise.resolve();
  }
}

export class TaskTemplatesClientMocked implements ITaskTemplatesClient {
  getTemplates(): Promise<TaskTemplateDto[]> {
    return Promise.resolve([...tasksMockStore.taskTemplates]);
  }

  getTemplate(id: number): Promise<TaskTemplateDto> {
    const found = tasksMockStore.taskTemplates.find(t => t.id === id);
    if (!found) return Promise.reject(new Error('Not found'));
    return Promise.resolve(found);
  }

  createTemplate(dto: TaskTemplateDto): Promise<void> {
    const id = tasksMockStore.nextId();
    const versionId = tasksMockStore.nextId();
    const template: TaskTemplateDto = {
      id,
      name: dto.name,
      description: dto.description,
      taskCategoryId: dto.taskCategoryId,
      versions: [
        {
          id: versionId,
          templateId: id,
          versionNumber: (dto.versions?.[0]?.versionNumber ?? 0) + 1,
          defaultTaskPriorityId: dto.versions?.[0]?.defaultTaskPriorityId ?? 1,
          expectedDurationMins: dto.versions?.[0]?.expectedDurationMins ?? null,
          isActive: true,
          isGlobalTemplate: dto.versions?.[0]?.isGlobalTemplate ?? true,
          steps: dto.versions?.[0]?.steps ?? [],
          requiresApproval: dto.versions?.[0]?.requiresApproval ?? false,
          defaultApproverPersonaId: dto.versions?.[0]?.defaultApproverPersonaId ?? null,
          defaultApproverUserId: dto.versions?.[0]?.defaultApproverUserId ?? null,
        },
      ],
    };
    tasksMockStore.taskTemplates.push(template);
    return Promise.resolve();
  }

  updateTemplate(id: number, dto: TaskTemplateDto): Promise<void> {
    const idx = tasksMockStore.taskTemplates.findIndex(t => t.id === id);
    if (idx >= 0) {
      tasksMockStore.taskTemplates[idx] = {
        ...tasksMockStore.taskTemplates[idx],
        name: dto.name,
        description: dto.description,
        taskCategoryId: dto.taskCategoryId,
        versions: dto.versions ? dto.versions.map(v => ({
          ...v,
          steps: v.steps ? v.steps.map(s => ({ ...s })) : [],
        })) : tasksMockStore.taskTemplates[idx].versions,
      };
    }
    return Promise.resolve();
  }

  deleteTemplate(id: number): Promise<void> {
    const idx = tasksMockStore.taskTemplates.findIndex(t => t.id === id);
    if (idx >= 0) tasksMockStore.taskTemplates.splice(idx, 1);
    return Promise.resolve();
  }
}

export class TaskCategoriesClientMocked implements ITaskCategoriesClient {
  getCategories(): Promise<TaskCategoryDto[]> {
    return Promise.resolve([...tasksMockStore.taskCategories]);
  }

  createCategory(dto: TaskCategoryDto): Promise<void> {
    const id = tasksMockStore.nextId();
    tasksMockStore.taskCategories.push({ ...dto, id, franchiseId: dto.franchiseId ?? 1 });
    return Promise.resolve();
  }

  updateCategory(id: number, dto: TaskCategoryDto): Promise<void> {
    const idx = tasksMockStore.taskCategories.findIndex(c => c.id === id);
    if (idx >= 0) tasksMockStore.taskCategories[idx] = { ...dto, id };
    return Promise.resolve();
  }

  deleteCategory(id: number): Promise<void> {
    const idx = tasksMockStore.taskCategories.findIndex(c => c.id === id);
    if (idx >= 0) tasksMockStore.taskCategories.splice(idx, 1);
    return Promise.resolve();
  }
}

export class TaskStatusesClientMocked implements ITaskStatusesClient {
  getStatuses(): Promise<TaskStatusDto[]> {
    return Promise.resolve([...tasksMockStore.taskStatuses]);
  }
}

export class TaskPrioritiesClientMocked implements ITaskPrioritiesClient {
  getPriorities(): Promise<TaskPriorityDto[]> {
    return Promise.resolve([...tasksMockStore.taskPriorities]);
  }
}

export class TaskSchedulesClientMocked implements ITaskSchedulesClient {
  getSchedules(): Promise<TaskScheduleDto[]> {
    return Promise.resolve([...tasksMockStore.taskSchedules]);
  }

  getSchedule(id: number): Promise<TaskScheduleDto> {
    const found = tasksMockStore.taskSchedules.find(s => s.id === id);
    if (!found) return Promise.reject(new Error('Not found'));
    return Promise.resolve(found);
  }

  createSchedule(dto: TaskScheduleDto): Promise<void> {
    tasksMockStore.taskSchedules.push({ ...dto, id: tasksMockStore.nextId() });
    return Promise.resolve();
  }

  updateSchedule(id: number, dto: TaskScheduleDto): Promise<void> {
    const idx = tasksMockStore.taskSchedules.findIndex(s => s.id === id);
    if (idx >= 0) tasksMockStore.taskSchedules[idx] = { ...dto, id };
    return Promise.resolve();
  }

  deleteSchedule(id: number): Promise<void> {
    const idx = tasksMockStore.taskSchedules.findIndex(s => s.id === id);
    if (idx >= 0) tasksMockStore.taskSchedules.splice(idx, 1);
    return Promise.resolve();
  }
}

export class TaskReportingDashboardClientMocked implements ITaskReportingDashboardClient {
  getDashboard(): Promise<SuccessResponse<TaskDashboardResponse>> {
    const summaries = tasksMockStore.tasks.map(toSummary);

    const statusMetrics: TaskStatusMetric[] = tasksMockStore.taskStatuses.map(s => ({
      statusId: s.id,
      statusName: s.name,
      count: summaries.filter(t => t.statusId === s.id).length,
    }));

    const priorityMetrics: TaskPriorityMetric[] = tasksMockStore.taskPriorities.map(p => ({
      priorityId: p.id,
      priorityName: p.name,
      count: summaries.filter(t => t.priorityId === p.id).length,
    }));

    const overdueMetrics: TaskOverdueMetric[] = [
      { storeOrFranchise: 'All', overdueCount: summaries.filter(t => t.isOverdue).length },
    ];

    const completionTrend: TaskCompletionTrendPoint[] = [
      {
        day: new Date().toISOString().substring(0, 10),
        createdCount: summaries.length,
        completedCount: summaries.filter(t => t.statusId === 3).length,
      },
    ];

    const totalTasks = summaries.length;
    const completedTasks = summaries.filter(t => t.statusId === 3).length;
    const overdueTasks = summaries.filter(t => t.isOverdue).length;
    const activeTasks = totalTasks - completedTasks;

    return Promise.resolve({
      data: {
        statusMetrics,
        overdueMetrics,
        completionTrend,
        priorityMetrics,
        totalTasks,
        activeTasks,
        completedTasks,
        overdueTasks,
      },
    });
  }
}
